/**
 * Action Engine — Adaptive Operating System
 *
 * Reads pre-aggregated Firestore data and computes confidence-gated,
 * executable recommendations. Speaks in actions, not analytics.
 *
 * 4 Capabilities:
 *   1. Adaptive Sending Volume  (risk warning)
 *   2. Segment-based Flow Selection  (priority)
 *   3. Objection-aware Template Switching  (focus)
 *   4. Momentum-based Focus Mode  (focus enforcement)
 */

import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { ref, get, update } from 'firebase/database';
import { firestore, database } from '../firebaseConfig';

// ─── Confidence helpers ──────────────────────────

function volumeConfidence(sent) {
  if (sent >= 50) return 'high';
  if (sent >= 20) return 'medium';
  return 'low';
}

function segmentConfidence(totalContacts) {
  if (totalContacts >= 15) return 'high';
  if (totalContacts >= 5) return 'medium';
  return 'low';
}

function templateConfidence(replyCount) {
  if (replyCount >= 8) return 'high';
  if (replyCount >= 3) return 'medium';
  return 'low';
}

function momentumConfidence(conversions) {
  if (conversions >= 5) return 'high';
  if (conversions >= 2) return 'medium';
  return 'low';
}

const CONF_PRIORITY = { high: 3, medium: 2, low: 1 };

// ─── Shared data loader (single Firestore read) ─

async function loadSegmentData(userId) {
  const snap = await getDocs(collection(firestore, `World/${userId}/signalPerformance`));
  const segments = [];
  snap.forEach(d => segments.push({ key: d.id, ...d.data() }));
  return segments;
}

function formatSegmentLabel(seg) {
  const s = seg.segment;
  if (!s) return seg.key;
  const parts = [s.offerType || '?'];
  if (s.b2b_b2c && s.b2b_b2c !== 'unknown') parts.push(s.b2b_b2c.toUpperCase());
  if (s.industry && s.industry !== 'unknown' && s.industry !== 'Other') parts.push(s.industry);
  parts.push(s.sizeProxy || '?');
  return parts.join(' · ');
}

// ─── 1. Adaptive Sending Volume ──────────────────

function computeVolumeActions(segments, templateStats) {
  const actions = [];

  const { totalSent, totalReplies } = templateStats;
  const conf = volumeConfidence(totalSent);
  if (conf === 'low') return actions;

  let hostileCount = 0;
  let spamAccCount = 0;
  let totalReplySubtypes = 0;

  for (const seg of segments) {
    const rs = seg.replySubtypes || {};
    for (const [st, count] of Object.entries(rs)) {
      totalReplySubtypes += count;
      if (st === 'hostile') hostileCount += count;
      if (st === 'spam_accusation') spamAccCount += count;
    }
  }

  const negativeCount = hostileCount + spamAccCount;
  const negativeRate = totalReplySubtypes > 0 ? negativeCount / totalReplySubtypes : 0;
  const replyRate = totalSent > 0 ? totalReplies / totalSent : 0;

  if (negativeRate > 0.15 && totalReplySubtypes >= 5) {
    actions.push({
      id: 'vol_risk_negative',
      type: 'volume',
      priority: 9,
      confidence: conf,
      risk: true,
      title: 'Sending Risk Detected',
      body: `${Math.round(negativeRate * 100)}% negative replies (${negativeCount} of ${totalReplySubtypes}). Reduce volume to protect your reputation.`,
      dataPoints: { negativeRate, negativeCount, totalReplySubtypes, hostileCount, spamAccCount },
      action: {
        label: 'Reduce Volume',
        executor: 'setQueueDuration',
        payload: { durationMinutes: 30 },
      },
      dismiss: true,
      createdAt: Date.now(),
    });
  }

  if (replyRate < 0.02 && totalSent >= 50) {
    actions.push({
      id: 'vol_low_reply',
      type: 'volume',
      priority: 7,
      confidence: conf,
      risk: true,
      title: 'Very Low Reply Rate',
      body: `Only ${Math.round(replyRate * 100)}% reply rate across ${totalSent} emails sent. Review your templates or target audience.`,
      dataPoints: { replyRate, totalSent, totalReplies },
      action: null,
      dismiss: true,
      createdAt: Date.now(),
    });
  }

  return actions;
}

// ─── 2. Segment-based Flow Selection ─────────────

function computeFlowActions(segments) {
  const actions = [];

  for (const seg of segments) {
    if ((seg.totalContacts || 0) < 5) continue;
    const conf = segmentConfidence(seg.totalContacts);
    if (conf === 'low') continue;

    const ws = seg.winningSequences || {};
    const bestSeq = Object.entries(ws).sort((a, b) => b[1] - a[1])[0];

    if (bestSeq && seg.conversionRate > 0) {
      const [seqName, wins] = bestSeq;
      const segLabel = formatSegmentLabel(seg);

      actions.push({
        id: `flow_winning_${seg.key}`,
        type: 'flow',
        priority: 6 + CONF_PRIORITY[conf],
        confidence: conf,
        risk: false,
        title: `Winning Sequence: ${seqName}`,
        body: `${segLabel} converts with "${seqName}" (${wins} wins, ${seg.conversionRate}% rate). Use this sequence for new contacts.`,
        dataPoints: {
          segmentKey: seg.key,
          conversionRate: seg.conversionRate,
          winningSequence: seqName,
          wins,
          totalContacts: seg.totalContacts,
        },
        action: null,
        dismiss: true,
        createdAt: Date.now(),
      });
    }
  }

  return actions;
}

// ─── 3. Objection-aware Template Switching ───────

const OBJECTION_MAP = {
  price_objection: {
    threshold: 0.4,
    title: 'Price Objections Dominating',
    suggestion: 'Switch to a value-first template that emphasizes ROI.',
  },
  wrong_person: {
    threshold: 0.3,
    title: 'Wrong Contacts',
    suggestion: 'Use a referral template that asks for the right person.',
  },
  no_need: {
    threshold: 0.5,
    title: 'No Need Detected',
    suggestion: 'Change your approach: different offer or different target audience for this segment.',
  },
  hostile: {
    threshold: 0.2,
    title: 'Hostile Reactions',
    suggestion: 'This segment reacts aggressively. Pause sending or completely change your approach.',
  },
};

function computeTemplateActions(segments) {
  const actions = [];

  for (const seg of segments) {
    const rs = seg.replySubtypes || {};
    const totalReplies = Object.values(rs).reduce((s, v) => s + v, 0);

    const conf = templateConfidence(totalReplies);
    if (conf === 'low') continue;

    const segLabel = formatSegmentLabel(seg);

    for (const [subtype, cfg] of Object.entries(OBJECTION_MAP)) {
      const count = rs[subtype] || 0;
      const rate = totalReplies > 0 ? count / totalReplies : 0;

      if (rate >= cfg.threshold) {
        actions.push({
          id: `tpl_${subtype}_${seg.key}`,
          type: 'template',
          priority: subtype === 'hostile' ? 8 : 5 + CONF_PRIORITY[conf],
          confidence: conf,
          risk: subtype === 'hostile',
          title: `${segLabel}: ${cfg.title}`,
          body: `${Math.round(rate * 100)}% of replies are "${subtype}" (${count}/${totalReplies}). ${cfg.suggestion}`,
          dataPoints: { segmentKey: seg.key, subtype, rate, count, totalReplies },
          action: null,
          dismiss: true,
          createdAt: Date.now(),
        });
      }
    }
  }

  return actions;
}

// ─── 4. Momentum-based Focus Mode ───────────────

function computeMomentumActions(segments) {
  const actions = [];
  if (segments.length === 0) return actions;

  const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  const sorted = [...segments].sort((a, b) => (b.conversionRate || 0) - (a.conversionRate || 0));

  const hotSegments = sorted.filter(s =>
    (s.meetings || 0) + (s.deals || 0) >= 2 &&
    s.updatedAt && (now - s.updatedAt) < TWO_WEEKS
  );

  if (hotSegments.length > 0) {
    const best = hotSegments[0];
    const conversions = (best.meetings || 0) + (best.deals || 0);
    const conf = momentumConfidence(conversions);

    const segLabel = formatSegmentLabel(best);

    actions.push({
      id: `mom_hot_${best.key}`,
      type: 'momentum',
      priority: 7 + CONF_PRIORITY[conf],
      confidence: conf,
      risk: false,
      title: `${segLabel} is Converting`,
      body: `${conversions} conversions, ${best.conversionRate}% rate. Focus on this segment.`,
      dataPoints: {
        segmentKey: best.key,
        conversions,
        conversionRate: best.conversionRate,
        avgTouches: best.avgTouchesToConvert,
        totalContacts: best.totalContacts,
      },
      action: {
        label: 'Focus Now',
        executor: 'filterToSegment',
        payload: { segmentKey: best.key, segment: best.segment },
      },
      dismiss: true,
      createdAt: Date.now(),
    });
  }

  const coldSegments = sorted.filter(s =>
    (s.totalContacts || 0) >= 10 &&
    (s.meetings || 0) + (s.deals || 0) === 0 &&
    s.updatedAt && (now - s.updatedAt) > TWO_WEEKS
  );

  for (const cold of coldSegments.slice(0, 2)) {
    const segLabel = formatSegmentLabel(cold);

    actions.push({
      id: `mom_cold_${cold.key}`,
      type: 'momentum',
      priority: 5,
      confidence: 'medium',
      risk: true,
      title: `${segLabel}: No Results`,
      body: `${cold.totalContacts} contacts, 0 conversions. Pause this segment or change your strategy.`,
      dataPoints: {
        segmentKey: cold.key,
        totalContacts: cold.totalContacts,
        daysSinceUpdate: Math.round((now - cold.updatedAt) / (24 * 60 * 60 * 1000)),
      },
      action: null,
      dismiss: true,
      createdAt: Date.now(),
    });
  }

  return actions;
}

// ─── 5. Warmup Health → Risk Cards ───────────────

async function computeWarmupActions(userId) {
  const actions = [];
  try {
    const healthSnap = await get(ref(database, `World/${userId}/emailHealth`));
    const warmupSnap = await get(ref(database, `World/${userId}/warmupStats`));
    const configSnap = await get(ref(database, `World/${userId}/warmupConfig`));
    if (!healthSnap.exists()) return actions;

    const healthData = healthSnap.val();
    const warmupData = warmupSnap.exists() ? warmupSnap.val() : {};
    const configData = configSnap.exists() ? configSnap.val() : {};

    for (const [emailKey, health] of Object.entries(healthData)) {
      if (!health.totalSent || health.totalSent < 10) continue;
      const bounceRate = (health.totalBounced || 0) / health.totalSent;
      const complaintRate = (health.totalComplained || 0) / health.totalSent;
      const warmup = warmupData[emailKey] || {};
      const config = configData[emailKey] || {};
      const email = health.email || emailKey.replace(/_/g, '.');
      const totalRescued = warmup.totalSpamRescued || 0;
      const totalWarmupSent = warmup.totalSent || 0;

      // Bounce rate warning
      if (bounceRate > 0.02) {
        const tier = bounceRate > 0.03 ? 'crit' : 'warn';
        actions.push({
          id: `warmup_bounce_${emailKey}_${tier}`,
          type: 'warmup_bounce_warning',
          title: `${email}: Bounce rate ${(bounceRate * 100).toFixed(1)}%`,
          body: bounceRate > 0.03
            ? 'Critical bounce rate (over 3%). Warmup is auto-throttled for this account, but outreach is NOT paused. Reduce sending volume and clean your contact list now to protect your domain.'
            : 'Bounce rate is rising. Consider reducing volume or cleaning your contact list.',
          confidence: 'high',
          risk: true,
          priority: bounceRate > 0.03 ? 10 : 7,
          dismiss: true,
        });
      }

      // Complaint warning
      if (complaintRate > 0.001) {
        const tier = complaintRate > 0.003 ? 'crit' : 'warn';
        actions.push({
          id: `warmup_complaint_${emailKey}_${tier}`,
          type: 'warmup_complaint_warning',
          title: `${email}: Complaint rate ${(complaintRate * 100).toFixed(2)}%`,
          body: complaintRate > 0.003
            ? 'Complaints are critically high. Sending volume reduced to protect reputation.'
            : 'Some recipients are marking your emails as spam. Review your templates.',
          confidence: 'high',
          risk: true,
          priority: complaintRate > 0.003 ? 9 : 6,
          dismiss: true,
        });
      }

      // Spam rescue ratio rising
      if (totalWarmupSent > 20 && totalRescued > 0) {
        const rescueRatio = totalRescued / totalWarmupSent;
        if (rescueRatio > 0.15) {
          const tier = rescueRatio > 0.3 ? 'high' : 'med';
          actions.push({
            id: `warmup_spam_${emailKey}_${tier}`,
            type: 'warmup_spam_placement',
            title: `${email}: ${Math.round(rescueRatio * 100)}% of warmup lands in spam`,
            body: 'Your emails are being filtered to spam more than usual. Check your domain DNS setup (SPF, DKIM, DMARC).',
            confidence: rescueRatio > 0.3 ? 'high' : 'medium',
            risk: true,
            priority: 5,
            dismiss: true,
          });
        }
      }

      // Not warming up — suggest it
      if (!config.enabled && health.totalSent > 50 && bounceRate < 0.01) {
        actions.push({
          id: `warmup_suggestion_${emailKey}`,
          type: 'warmup_suggestion',
          title: `Enable warmup for ${email}`,
          body: 'This account has decent health but isn\'t warming up. Enable warmup to build reputation further.',
          confidence: 'medium',
          risk: false,
          priority: 3,
          dismiss: true,
        });
      }
    }
  } catch (_) {}
  return actions;
}

// ─── Master compute ──────────────────────────────

export async function computeAllActions(userId) {
  if (!userId) return [];

  try {
    const [segments, templatesSnap] = await Promise.all([
      loadSegmentData(userId),
      get(ref(database, `World/${userId}/settings/customTopics`)),
    ]);

    const templates = templatesSnap.exists() ? templatesSnap.val() : {};
    let totalSent = 0;
    let totalReplies = 0;
    for (const t of Object.values(templates)) {
      totalSent += t.stats?.totalSent || 0;
      totalReplies += t.stats?.totalReplies || 0;
    }

    const volume = computeVolumeActions(segments, { totalSent, totalReplies });
    const flow = computeFlowActions(segments);
    const template = computeTemplateActions(segments);
    const momentum = computeMomentumActions(segments);
    const warmup = await computeWarmupActions(userId);

    const all = [...volume, ...flow, ...template, ...momentum, ...warmup];

    return all
      .filter(a => a.confidence !== 'low')
      .sort((a, b) => {
        if (a.risk !== b.risk) return a.risk ? -1 : 1;
        return b.priority - a.priority;
      })
      .slice(0, 6);
  } catch (e) {
    console.warn('[ActionEngine] computeAllActions error:', e.message);
    return [];
  }
}

// ─── Action Executors ────────────────────────────

export async function executeAction(userId, action) {
  if (!userId || !action?.action) return false;

  const { executor, payload } = action.action;

  try {
    switch (executor) {
      case 'setQueueDuration': {
        const minutes = payload.durationMinutes || 20;
        await update(ref(database, `World/${userId}/settings`), {
          email_queue_duration: String(minutes),
        });
        return true;
      }

      case 'filterToSegment': {
        return true;
      }

      default:
        console.warn(`[ActionEngine] Unknown executor: ${executor}`);
        return false;
    }
  } catch (e) {
    console.warn('[ActionEngine] executeAction error:', e.message);
    return false;
  }
}

// ─── Follow-up Reminders ─────────────────────────
//
// User-created reminders live in `World/{userId}/reminders`. They are
// surfaced in the Action Center so a cold caller gets nudged to follow up
// on a specific contact at the right time.

export async function loadReminders(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(collection(firestore, `World/${userId}/reminders`));
    const reminders = [];
    snap.forEach(d => reminders.push({ id: d.id, ...d.data() }));
    return reminders
      .filter(r => r.status !== 'done')
      .sort((a, b) => (a.dueAt || 0) - (b.dueAt || 0));
  } catch (e) {
    console.warn('[ActionEngine] loadReminders error:', e.message);
    return [];
  }
}

export async function completeReminder(userId, reminderId) {
  if (!userId || !reminderId) return false;
  try {
    await deleteDoc(doc(firestore, `World/${userId}/reminders/${reminderId}`));
    return true;
  } catch (e) {
    console.warn('[ActionEngine] completeReminder error:', e.message);
    return false;
  }
}
